/**
 * ProcessSLAStatus widget — SLA compliance per process definition.
 *
 * Migrated to live API in Wave 3.6. Reads GET /v1/processes/instances
 * + GET /v1/processes/definitions in parallel. Every ProcessInstance
 * has a \`sla_status\` enum ('on_track' | 'at_risk' | 'breached' | null)
 * plus an \`sla_deadline\` timestamp. Client-side aggregation groups
 * instances by definition_id and produces one row per definition with
 * compliance %, at-risk count, and breach count.
 *
 * compliance = (instances not at-risk and not breached) / total × 100
 *
 * When the backend ships a real /v1/processes/analytics SLA endpoint
 * (part of ADR-015+ engine work), the widget can drop the instances
 * fetch and switch to whatever shape analytics returns.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, processesApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { queryDashboard } from '../../../../api/dashboardQuery';
import type { ProcessInstance } from '../../../../api/processes';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { TimerOutlined } from '@mui/icons-material';

interface DefinitionLite { id: string; name: string }

interface SLARow {
  definitionId: string;
  processName: string;
  total: number;
  onTrack: number;
  atRisk: number;
  breached: number;
  compliance: number; // 0..100
}

function slaColor(rate: number): '#4CAF50' | '#FF9800' | '#F44336' {
  if (rate >= 95) return '#4CAF50';
  if (rate >= 85) return '#FF9800';
  return '#F44336';
}

const ProcessSLAStatus: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [instances, setInstances] = useState<ProcessInstance[]>([]);
  const [definitions, setDefinitions] = useState<DefinitionLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [instRaw, defRaw] = await Promise.all([
          queryDashboard(
            'process-instances',
            () => processesApi.processInstancesApi.list(),
          ),
          queryDashboard(
            'process-definitions',
            () => processesApi.processDefinitionsApi.list(),
          ),
        ]);
        if (cancelled) return;
        setInstances(instRaw);
        // definitions still `unknown`-typed in api/processes.ts (Wave 3.3
        // will type them), but we only need id + name for the display
        // label. Defensive pick keeps this working regardless of the
        // exact field shape.
        const defs = (defRaw as Array<Record<string, unknown>>).map((d) => ({
          id: String(d.id ?? ''),
          name: String(d.name ?? d.display_name ?? t('dashboard:widgets.processSla.unnamedProcess')),
        }));
        setDefinitions(defs);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.processSla.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  const rows: SLARow[] = useMemo(() => {
    const byDef = new Map<string, SLARow>();
    const defName = new Map(definitions.map((d) => [d.id, d.name]));
    for (const inst of instances) {
      const defId = inst.definition_id;
      if (!defId) continue;
      const prev = byDef.get(defId) ?? {
        definitionId: defId,
        processName: defName.get(defId) ?? t('dashboard:widgets.processSla.unknownProcess'),
        total: 0, onTrack: 0, atRisk: 0, breached: 0, compliance: 0,
      };
      prev.total += 1;
      if (inst.sla_status === 'breached') prev.breached += 1;
      else if (inst.sla_status === 'at_risk') prev.atRisk += 1;
      else prev.onTrack += 1;
      byDef.set(defId, prev);
    }
    return Array.from(byDef.values())
      .map((r) => ({
        ...r,
        compliance: r.total === 0
          ? 100
          : Math.round(((r.total - r.breached - r.atRisk) / r.total) * 100),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
  }, [instances, definitions, t]);

  const overall = rows.length === 0
    ? 100
    : Math.round(rows.reduce((s, r) => s + r.compliance, 0) / rows.length);
  const totalAtRisk = rows.reduce((s, r) => s + r.atRisk, 0);
  const totalBreached = rows.reduce((s, r) => s + r.breached, 0);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (rows.length === 0) {
    return (
      <WidgetEmptyState icon={TimerOutlined} title={t('dashboard:widgets.processSla.noTrackedInstances')} />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Summary row */}
      <Box
        onClick={() => navigate('/processes/instances/active')}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, pb: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          cursor: 'pointer',
          borderRadius: 1, mx: -0.5, px: 0.5,
          transition: 'background-color 120ms',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: slaColor(overall),
            lineHeight: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {overall}%
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{t('dashboard:widgets.processSla.overallSla')}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <LinearProgress variant="determinate" value={overall}
            sx={{ height: 6, borderRadius: 8, bgcolor: 'grey.100', mb: 0.5,
              '& .MuiLinearProgress-bar': { bgcolor: slaColor(overall) } }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main', lineHeight: 1 }}>
              {totalAtRisk}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t('dashboard:widgets.processSla.atRisk')}</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main', lineHeight: 1 }}>
              {totalBreached}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{t('dashboard:widgets.processSla.breached')}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Per-process breakdown */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.map((r) => (
          <Box key={r.definitionId}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.4 }}>
              <Typography variant="caption"
                sx={{ fontWeight: 600, color: 'text.primary', flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.processName}
              </Typography>
              {(r.atRisk > 0 || r.breached > 0) && (
                <WarningAmberOutlined sx={{ fontSize: '0.8rem',
                  color: r.breached > 0 ? 'error.main' : 'warning.main', mr: 0.5 }} />
              )}
              <Typography variant="caption"
                sx={{ fontWeight: 700, color: slaColor(r.compliance),
                  fontSize: '0.75rem', minWidth: 32, textAlign: 'right' }}>
                {r.compliance}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={r.compliance}
              sx={{ height: 4, borderRadius: 8, bgcolor: 'grey.100',
                '& .MuiLinearProgress-bar': { bgcolor: slaColor(r.compliance) } }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ProcessSLAStatus;
